import { JobCode, TimeOffReason, ScheduleShift, Employee, ShiftFormData, TimeOffReasonRequest } from '../../types';
import { GeneralSettings, GeneralSettingsRequest } from '../../types/scheduler';
import { getTimeOffReasons as apiGetTimeOffReasons, createTimeOffReason as apiCreateTimeOffReason, updateTimeOffReason as apiUpdateTimeOffReason, deleteTimeOffReason as apiDeleteTimeOffReason, getGeneralSettings as apiGetGeneralSettings, createGeneralSettings as apiCreateGeneralSettings, updateGeneralSettings as apiUpdateGeneralSettings } from '../API/scheduler';

// TODO: Replace with actual API calls when endpoints are available
export const schedulerService = {
    // Employees
    getEmployees: async (): Promise<Employee[]> => {
        // Fallback dummy data
        return [
            { id: '1', name: 'John Doe' },
            { id: '2', name: 'Jane Smith' },
            { id: '3', name: 'Mike Johnson' }
        ];
    },

    // Shifts
    getShifts: async (_startDate: Date, _endDate: Date): Promise<ScheduleShift[]> => {
        // Fallback dummy data
        return [
            {
                id: '1',
                employeeId: '1',
                jobCodeId: '1',
                date: '2025-11-03',
                startTime: '09:00',
                endTime: '17:00'
            }
        ];
    },

    addShift: async (shift: ShiftFormData): Promise<ScheduleShift> => {
        // TODO: Implement API call
        return { id: Math.random().toString(), ...shift };
    },

    updateShift: async (shift: ScheduleShift): Promise<ScheduleShift> => {
        // TODO: Implement API call
        return shift;
    },

    deleteShift: async (_id: string): Promise<boolean> => {
        // TODO: Implement API call
        return true;
    },

    // Business Date Utils
    getBusinessDate: (date: Date = new Date()): Date => {
        // Convert date to business date logic here
        // This is a simplified version - you might need more complex logic
        // based on your specific business rules
        const businessStartHour = 0; // midnight
        const d = new Date(date);
        if (d.getHours() < businessStartHour) {
            d.setDate(d.getDate() - 1);
        }
        return d;
    },

    getTimeSlots: (): string[] => {
        const slots: string[] = [];
        const totalMinutesInDay = 24 * 60;
        const increment = 15; // 15-minute increments

        for (let minutes = 0; minutes < totalMinutesInDay; minutes += increment) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            slots.push(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`);
        }

        return slots;
    },
    // Job Codes
    getJobCodes: async (): Promise<JobCode[]> => {
        // Fallback dummy data
        return [
            { id: '1', name: 'Manager', color: '#FF0000' },
            { id: '2', name: 'Hourly Employee', color: '#0000FF' }
        ];
    },

    addJobCode: async (jobCode: Omit<JobCode, 'id'>): Promise<JobCode> => {
        // TODO: Implement API call
        return { id: Math.random().toString(), ...jobCode };
    },

    updateJobCode: async (jobCode: JobCode): Promise<JobCode> => {
        // TODO: Implement API call
        return jobCode;
    },

    deleteJobCode: async (_id: string): Promise<boolean> => {
        // TODO: Implement API call
        return true;
    },

    // Time Off Reasons
    getTimeOffReasons: async (): Promise<TimeOffReason[]> => {
        try {
            return await apiGetTimeOffReasons();
        } catch (error) {
            console.error('Error fetching time off reasons:', error);
            // Fallback dummy data
            return [
                { 
                    "@id": "/api/scheduler_time_off_reasons/1",
                    "@type": "SchedulerTimeOffReasons",
                    id: 1, 
                    name: 'Time Off',
                    description: null,
                    active: true,
                    deleted: false,
                    created_at: null,
                    updated_at: null
                }
            ];
        }
    },

    addTimeOffReason: async (reason: TimeOffReasonRequest): Promise<TimeOffReason> => {
        try {
            return await apiCreateTimeOffReason(reason);
        } catch (error) {
            console.error('Error creating time off reason:', error);
            throw error;
        }
    },

    updateTimeOffReason: async (id: number, reason: TimeOffReasonRequest): Promise<TimeOffReason> => {
        try {
            return await apiUpdateTimeOffReason(id, reason);
        } catch (error) {
            console.error('Error updating time off reason:', error);
            throw error;
        }
    },

    deleteTimeOffReason: async (id: string): Promise<boolean> => {
        try {
            await apiDeleteTimeOffReason(parseInt(id));
            return true;
        } catch (error) {
            console.error('Error deleting time off reason:', error);
            throw error;
        }
    }
    ,
    // Scheduler Settings persistence
    getSettings: async (): Promise<GeneralSettings> => {
        try {
            const settings = await apiGetGeneralSettings();
            if (settings) {
                // Ensure backward compatibility with old data that might not have the new fields
                return {
                    ...settings,
                    minShiftLength: settings.minShiftLength || 2,
                    maxShiftLength: settings.maxShiftLength || 16,
                };
            }
        } catch (error) {
            console.error('Error fetching general settings:', error);
        }

        // Return defaults if no settings found
        return {
            workplace: null,
            ageOfMinor: 18,
            overtimeDailyHoursAt: 8,
            overtimeDailyWarningAt: 7,
            overtimeWeeklyHoursAt: 40,
            timeIncrement: 15,
            overtimeWeeklyWarningAt: 36,
            defaultShiftLengthHours: 8,
            minShiftLength: 2,
            maxShiftLength: 16,
        };
    },

    saveSettings: async (settings: GeneralSettingsRequest, existingSettings?: GeneralSettings | null): Promise<GeneralSettings> => {
        try {
            if (existingSettings && existingSettings.id) {
                // Update existing settings
                return await apiUpdateGeneralSettings(existingSettings.id, settings);
            } else {
                // Create new settings
                return await apiCreateGeneralSettings(settings);
            }
        } catch (error) {
            console.error('Error saving general settings:', error);
            throw error;
        }
    },

    // Permissions (stored as simple object keyed by permission name)
    getPermissions: async (): Promise<any> => {
        const raw = localStorage.getItem('scheduler-permissions');
        if (raw) {
            try {
                return JSON.parse(raw);
            } catch (e) {
                return null;
            }
        }

        // defaults
        return {
            scheduler: false,
            schedulerAllEmployees: false,
            approveRequests: false,
        };
    },

    savePermissions: async (perms: any): Promise<void> => {
        localStorage.setItem('scheduler-permissions', JSON.stringify(perms));
    }
};